// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

// Lists discovery responses that the attorney has marked visible to this
// client. Drafts and other clients' responses never reach this list — the
// backend filters by status='visible_to_client'|'locked' AND client_id =
// req.clientId.
//
// Clicking a row drops the client into the per-response workspace.

const STATUS_STYLES = {
  visible_to_client: { bg: '#e8f0fb', fg: '#2f5496', border: '#bcd4ee', label: 'Open for review' },
  locked:            { bg: '#f0f0f0', fg: '#555',    border: '#ddd',     label: 'Closed' },
};

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ClientDiscoveryListPage() {
  const navigate = useNavigate();
  const [responses, setResponses] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  useEffect(() => {
    setLoading(true);
    api.listClientDiscovery()
      .then(rows => { setResponses(rows || []); setError(''); })
      .catch(err => setError(err.message || 'Could not load discovery responses.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;
  }

  return (
    <div>
      <p className="page-subtitle" style={{ marginBottom: 4, marginTop: 0 }}>Discovery</p>
      <h1 className="page-title">Discovery Responses</h1>
      <p className="page-subtitle">
        Below are the discovery requests your attorney has shared with you. Click one to review
        and answer the questions. Your answers save as you go and your attorney will see them
        right away.
      </p>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {responses.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
          You don't have any discovery responses to review right now. Your attorney will let you
          know when something is ready.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0F1838', color: '#fff' }}>
                  <th style={{ padding: '8px 14px', textAlign: 'left',  fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Title</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left',  fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, width: 150 }}>Status</th>
                  <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, width: 120 }}>Questions</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left',  fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, width: 130 }}>Shared</th>
                </tr>
              </thead>
              <tbody>
                {responses.map((r, i) => {
                  const s = STATUS_STYLES[r.status] || STATUS_STYLES.visible_to_client;
                  return (
                    <tr key={r.id}
                      onClick={() => navigate(`/discovery/${r.id}`)}
                      style={{ borderBottom: '1px solid #eee', cursor: 'pointer', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '12px 14px', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                        <strong>{r.title || 'Discovery Response'}</strong>
                        {r.docket_no && (
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                            Docket {r.docket_no}{r.division ? ` · Div. ${r.division}` : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 3,
                          background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
                          fontFamily: 'Arial, sans-serif', display: 'inline-block', whiteSpace: 'nowrap',
                        }}>{s.label}</span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555' }}>
                        {r.question_count ?? 0}
                      </td>
                      <td style={{ padding: '12px 14px', fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555' }}>
                        {fmtDate(r.updated_at || r.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
